// "New game" setup screen (play-modes-and-rulesets spec §12): pick who's
// playing (a preset card), optionally Customize (rules, world, players,
// content), share the rules as a code, and Start. Quick start skips the
// choices and plays the last-used setup, or Standard.
//
// The screen only edits a SetupDraft; App turns it into a MatchSetup and
// boots the sim with it (see onStart).

import { validateRuleset } from '@aod/protocol';
import { Button } from '@aod/ui/components/ui/button';
import { cn } from '@aod/ui/lib/utils';
import { ArrowLeft, ChevronDown, Play, RotateCcw, SlidersHorizontal, Zap } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import {
  ContentTab,
  type Notice,
  PlayersTab,
  RulesTab,
  type SetupTab,
  SetupTabList,
  WorldTab,
} from './CustomizeTabs.js';
import { PresetCard } from './PresetCard.js';
import { ShareCodePanel } from './ShareCodePanel.js';
import { useRovingRadio } from './controls.js';
import { draftTitle, presetName, summaryParts, summaryText } from './presentation.js';
import {
  PRESET_IDS,
  type PresetDefId,
  type SetupDraft,
  defaultDraft,
  draftFromPreset,
  isCustomised,
  resetDraft,
} from './setupModel.js';

interface Props {
  /** Draft to open with — normally the last-used setup. Defaults to Standard. */
  readonly initialDraft?: SetupDraft | null;
  /** What Quick start plays. Defaults to `initialDraft`. */
  readonly quickStartDraft?: SetupDraft | null;
  readonly onStart: (draft: SetupDraft) => void;
  readonly onBack: () => void;
}

export function SetupScreen({ initialDraft, quickStartDraft, onStart, onBack }: Props) {
  const t = useT();
  const id = useId();
  const [draft, setDraftRaw] = useState<SetupDraft>(() => initialDraft ?? defaultDraft());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [tab, setTab] = useState<SetupTab>('rules');
  const [notice, setNotice] = useState<Notice | null>(null);
  const quick = quickStartDraft ?? initialDraft ?? defaultDraft();

  const setDraft = (next: SetupDraft) => setDraftRaw(next);
  const pickPreset = (p: PresetDefId) => {
    setDraftRaw(draftFromPreset(p));
    setNotice(null);
  };
  const presetKeys = useRovingRadio(PRESET_IDS, draft.basedOn, pickPreset);

  const problems = useMemo(() => validateRuleset(draft.ruleset), [draft.ruleset]);
  const customised = isCustomised(draft);
  const summary = summaryText(t, draft);
  const baseName = presetName(t, draft.basedOn);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-background text-foreground">
      {/* Soft painted wash behind the header, in both themes. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent"
      />
      <header className="relative z-10 flex items-center gap-2 border-b border-border/60 bg-background/80 px-3 py-2 backdrop-blur sm:px-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          aria-label={t('setup.back')}
          className="shrink-0"
        >
          <ArrowLeft aria-hidden />
          <span className="hidden sm:inline">{t('setup.back')}</span>
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-center text-lg font-extrabold tracking-wide sm:text-xl">
          {t('setup.title')}
        </h1>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onStart(quick)}
          aria-label={t('setup.quickStartLabel', { name: draftTitle(t, quick) })}
          title={t('setup.quickStartLabel', { name: draftTitle(t, quick) })}
          data-testid="setup-quick-start"
          {...appDom({
            id: 'setup-quick-start',
            type: 'Button',
            role: 'cta',
            scope: 'header',
            label: 'Quick start',
          })}
          className="shrink-0 border-primary/50 font-semibold"
        >
          <Zap className="text-primary" aria-hidden />
          {t('setup.quickStart')}
          <span className="hidden text-muted-foreground md:inline">· {draftTitle(t, quick)}</span>
        </Button>
      </header>

      <main
        data-testid="setup-screen"
        {...appDom({ id: 'setup-screen', type: 'Screen', scope: 'main', label: 'New game setup' })}
        className="relative z-0 flex-1 overflow-y-auto overflow-x-hidden"
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 pb-6 pt-4 sm:px-6 lg:pt-6">
          <section aria-labelledby={`${id}-who`} className="flex flex-col gap-3">
            <div>
              <h2 id={`${id}-who`} className="text-xl font-bold sm:text-2xl">
                {t('setup.who')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('setup.whoHint')}</p>
            </div>
            <div
              ref={presetKeys.ref}
              role="radiogroup"
              aria-labelledby={`${id}-who`}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
            >
              {PRESET_IDS.map((p) => (
                <PresetCard
                  key={p}
                  id={p}
                  selected={draft.basedOn === p}
                  focusable={draft.basedOn === p}
                  onSelect={pickPreset}
                  onKeyDown={presetKeys.onKeyDown}
                />
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-3 rounded-xl border border-border bg-card/60">
            <div className="flex flex-wrap items-center gap-2 p-2 sm:p-3">
              <button
                type="button"
                aria-expanded={customizeOpen}
                aria-controls={`${id}-customize`}
                onClick={() => setCustomizeOpen((v) => !v)}
                data-testid="setup-customize"
                {...appDom({
                  id: 'setup-customize',
                  type: 'Button',
                  role: 'button',
                  scope: 'main',
                  label: 'Customize',
                })}
                className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg px-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <SlidersHorizontal className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{t('setup.customize')}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {t('setup.customizeHint')}
                  </span>
                </span>
                <ChevronDown
                  aria-hidden
                  className={cn(
                    'size-5 shrink-0 text-muted-foreground transition-transform',
                    customizeOpen && 'rotate-180',
                  )}
                />
              </button>
              {customised ? (
                <div className="flex w-full items-center justify-between gap-2 rounded-lg bg-primary/10 px-3 py-1.5 sm:w-auto">
                  <span className="text-sm font-medium" data-testid="setup-custom-label">
                    {t('setup.custom', { name: baseName })}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDraftRaw(resetDraft(draft));
                      setNotice(null);
                    }}
                    data-testid="setup-reset"
                    {...appDom({
                      id: 'setup-reset',
                      type: 'Button',
                      role: 'button',
                      scope: 'main',
                    })}
                  >
                    <RotateCcw aria-hidden />
                    {t('setup.reset', { name: baseName })}
                  </Button>
                </div>
              ) : null}
            </div>
            {customizeOpen ? (
              <div id={`${id}-customize`} className="flex flex-col gap-4 px-3 pb-4 sm:px-4">
                <SetupTabList tab={tab} onTab={setTab} idBase={id} />
                <div
                  id={`${id}-panel`}
                  role="tabpanel"
                  aria-labelledby={`${id}-tab-${tab}`}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: WAI-ARIA tabs pattern — the panel is focusable when it has no focusable content at its start
                  tabIndex={0}
                  className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {tab === 'rules' ? (
                    <RulesTab
                      draft={draft}
                      onChange={setDraft}
                      problems={problems}
                      notice={notice}
                      onNotice={setNotice}
                    />
                  ) : tab === 'world' ? (
                    <WorldTab draft={draft} onChange={setDraft} />
                  ) : tab === 'players' ? (
                    <PlayersTab draft={draft} onChange={setDraft} />
                  ) : (
                    <ContentTab draft={draft} onChange={setDraft} />
                  )}
                </div>
              </div>
            ) : null}
          </section>

          <ShareCodePanel
            draft={draft}
            onLoad={(next) => {
              setDraftRaw(next);
              setNotice(null);
            }}
          />
        </div>
      </main>

      <footer className="relative z-10 border-t border-border/60 bg-background/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6">
          <p
            aria-label={t('setup.summary.label')}
            data-testid="setup-summary"
            {...appDom({ id: 'setup-summary', type: 'Text', role: 'text', scope: 'footer' })}
            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
          >
            {summaryParts(t, draft).map((part, i) => (
              <span
                key={part}
                className={cn(i === 0 ? 'font-bold text-foreground' : 'text-muted-foreground')}
              >
                {i > 0 ? (
                  <span aria-hidden className="mr-2 text-muted-foreground/60">
                    ·
                  </span>
                ) : null}
                {part}
              </span>
            ))}
            {problems.length > 0 ? (
              <span className="w-full text-xs font-medium text-destructive">
                {t('setup.problemCount')}
              </span>
            ) : null}
          </p>
          <Button
            type="button"
            size="lg"
            disabled={problems.length > 0}
            onClick={() => onStart(draft)}
            aria-label={t('setup.startLabel', { summary })}
            data-testid="setup-start"
            {...appDom({
              id: 'setup-start',
              type: 'Button',
              role: 'cta',
              scope: 'footer',
              label: 'Start the match',
            })}
            className="h-12 w-full px-10 text-base font-bold uppercase tracking-[0.15em] sm:w-auto"
            style={{
              background: 'linear-gradient(135deg, #f4d28a 0%, #c49b4c 50%, #8b6914 100%)',
              color: '#1a0f02',
              boxShadow:
                '0 4px 24px rgba(196,155,76,0.35), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.2)',
            }}
          >
            <Play className="fill-current" aria-hidden />
            {t('setup.start')}
          </Button>
        </div>
      </footer>
    </div>
  );
}
